"use client";

import { useState } from "react";
import Link from "next/link";
import {
  Handshake,
  Briefcase,
  Headphones,
  Users,
  Landmark,
  HeartPulse,
  GraduationCap,
  Scale,
  Home,
  Building2,
  ShoppingBag,
  Target,
  ChevronDown,
} from "lucide-react";

const sectors = [
  { name: "Sales & Fundraising", icon: Handshake, slug: "sales-fundraising" },
  { name: "B2B Sales Teams", icon: Briefcase, slug: "b2b-sales" },
  { name: "Klantenservice", icon: Headphones, slug: "klantenservice" },
  { name: "Recruitment & HR", icon: Users, slug: "recruitment-hr" },
  { name: "Financieel Advies", icon: Landmark, slug: "financieel-advies" },
  { name: "Gezondheidszorg", icon: HeartPulse, slug: "gezondheidszorg" },
  { name: "Onderwijs", icon: GraduationCap, slug: "onderwijs" },
  { name: "Juridisch", icon: Scale, slug: "juridisch" },
  { name: "Vastgoed", icon: Home, slug: "vastgoed" },
  { name: "Overheid", icon: Building2, slug: "overheid" },
  { name: "Horeca & Retail", icon: ShoppingBag, slug: "horeca-retail" },
  { name: "Coaching", icon: Target, slug: "coaching" },
];

const navLinks = [
  { href: "#features", label: "Features" },
  { href: "#pricing", label: "Aanpak" },
  { href: "#faq", label: "FAQ" },
  { href: "#contact", label: "Contact" },
];

export default function MobileNav() {
  const [open, setOpen] = useState(false);
  const [sectorenOpen, setSectorenOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-300 transition-colors hover:text-white focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none md:hidden"
        aria-label={open ? "Menu sluiten" : "Menu openen"}
        aria-expanded={open}
      >
        {open ? (
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        )}
      </button>

      {open && (
        <div className="absolute top-full right-0 left-0 border-b border-white/5 bg-[#030712]/95 backdrop-blur-xl md:hidden">
          <div className="flex flex-col gap-1 px-6 py-4">
            {navLinks.slice(0, 1).map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm text-gray-300 transition-colors hover:bg-white/5 hover:text-white"
              >
                {link.label}
              </a>
            ))}

            {/* Sectoren expandable */}
            <button
              onClick={() => setSectorenOpen((v) => !v)}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm text-gray-300 transition-colors hover:bg-white/5 hover:text-white"
              aria-expanded={sectorenOpen}
            >
              Sectoren
              <ChevronDown
                className={`h-4 w-4 transition-transform ${sectorenOpen ? "rotate-180" : ""}`}
              />
            </button>
            {sectorenOpen && (
              <div className="ml-3 flex flex-col gap-0.5 border-l border-white/5 pl-3">
                {sectors.map((sector) => (
                  <Link
                    key={sector.slug}
                    href={`/sectoren/${sector.slug}`}
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-400 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    <sector.icon className="h-3.5 w-3.5 text-blue-400" />
                    {sector.name}
                  </Link>
                ))}
                <Link
                  href="/sectoren"
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-blue-400 transition-colors hover:text-blue-300"
                >
                  Alle sectoren →
                </Link>
              </div>
            )}

            {navLinks.slice(1).map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm text-gray-300 transition-colors hover:bg-white/5 hover:text-white"
              >
                {link.label}
              </a>
            ))}

            <Link
              href="/blog"
              onClick={() => setOpen(false)}
              className="rounded-lg px-3 py-2.5 text-sm text-gray-300 transition-colors hover:bg-white/5 hover:text-white"
            >
              Blog
            </Link>
            <Link
              href="/kennisbank"
              onClick={() => setOpen(false)}
              className="rounded-lg px-3 py-2.5 text-sm text-gray-300 transition-colors hover:bg-white/5 hover:text-white"
            >
              Kennisbank
            </Link>
            <Link
              href="/over-ons"
              onClick={() => setOpen(false)}
              className="rounded-lg px-3 py-2.5 text-sm text-gray-300 transition-colors hover:bg-white/5 hover:text-white"
            >
              Over ons
            </Link>

            <a
              href="/login"
              onClick={() => setOpen(false)}
              className="mt-2 rounded-lg border border-white/20 px-4 py-2.5 text-center text-sm font-medium text-gray-300 transition-colors hover:border-white/40 hover:text-white"
            >
              Inloggen
            </a>
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              className="rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-2.5 text-center text-sm font-semibold text-white transition-[box-shadow,filter,transform] hover:brightness-110 active:scale-[0.98]"
            >
              Ontdek de mogelijkheden
            </a>
          </div>
        </div>
      )}
    </>
  );
}
